import React, { useCallback } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { token } from '@atlaskit/tokens';

import { MorphingShapeLoader } from '@atlassian/rovo-platform-ui-components/primitives/MorphingShapeLoader';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';
import { EllipsesAnimation } from '@atlassian/rovo-platform-ui-components/primitives/EllipsesAnimation';
import { ShimmerText } from '@atlassian/rovo-platform-ui-components/primitives/ShimmerText';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { useCancelledAnalytics } from '../analytics.ts';
import { useSuggestionsLoadingProgress } from '../useSuggestionsLoadingProgress.ts';
import {
	useRotatingLoadingMessage,
	useSuggestionsLoadingMessages,
} from '../useRotatingLoadingMessage.ts';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		height: '100%',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	loadingContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		width: '80%',
	},
});

const i18n = defineMessages({
	cancelButton: {
		id: 'suggestions-panel.loading-state.cancel-button',
		defaultMessage: 'Stop suggesting',
		description:
			'Button label to cancel/stop Rovo from generating suggestions in the suggestions panel',
	},
});

type SuggestionsLoadingStateProps = {
	/**
	 * Name of the agent doing the review, used to name it in the first loading message.
	 * Falls back to the generic Rovo copy when absent.
	 */
	agentName?: string;
	api?: FullPageEditorPresetAPI;
	/**
	 * `true` while the reviewing agent is still loading. The loading message then holds on
	 * "Preparing agent" (no rotation) until `agentName` resolves.
	 */
	isAgentLoading?: boolean;
	/**
	 * Epoch ms when the thinking session started. Anchors progress to the floating bar.
	 */
	startedAt?: number | null;
	/** Message rotation starts when the agent is ready; defaults to the session start. */
	rotationStartedAt?: number | null;
	/** Shared facade target so this bar fills to the same value as the floating bar. */
	progressTarget?: number | null;
};

export const SuggestionsLoadingState = ({
	agentName,
	api,
	isAgentLoading,
	startedAt,
	rotationStartedAt,
	progressTarget,
}: SuggestionsLoadingStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const loadingMessages = useSuggestionsLoadingMessages({ agentName, isAgentLoading });
	const loadingMessage = useRotatingLoadingMessage(loadingMessages, rotationStartedAt ?? startedAt);
	const progress = useSuggestionsLoadingProgress({ startedAt, progressTarget });
	const fireCancelledAnalytics = useCancelledAnalytics(api?.analytics?.actions.fireAnalyticsEvent);

	const handleCancel = useCallback(() => {
		fireCancelledAnalytics();
		api?.aiSuggestions?.actions?.setThinkingState('idle');
	}, [api, fireCancelledAnalytics]);

	return (
		<div css={styles.container} data-testid="suggestions-panel-loading-state">
			<div css={styles.loadingContainer}>
				<MorphingShapeLoader testId="suggestions-panel-thinking-indicator" size="large" />
				<ShimmerText size="medium">
					{loadingMessage}
					<EllipsesAnimation isAnimated />
				</ShimmerText>
				<RainbowProgressBar
					testId="suggestions-panel-progress-bar"
					label={loadingMessage}
					progress={progress}
					appearance="static-rainbow"
				/>
			</div>
			<Button onClick={handleCancel}>{formatMessage(i18n.cancelButton)}</Button>
		</div>
	);
};
